import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, Loader2, LogOut, Save, Package, ShoppingCart, Palette, Truck, CreditCard } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { mk } from "../../lib/marketApi";
import { ProductsTab } from "../../components/market/admin/ProductsTab";
import { OrdersTab } from "../../components/market/admin/OrdersTab";
import { DesignTab } from "../../components/market/admin/DesignTab";
import { DeliveryTab, PaymentsTab } from "../../components/market/admin/SettingsTabs";
import { inputCls } from "../../components/market/admin/ui";

const tokenKey = (slug) => `mk_owner_${slug}`;

const OwnerLogin = ({ slug, onLogin }) => {
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      onLogin(await mk(`/s/${slug}/owner/login`, { method: "POST", body: f }));
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };
  const creator = async () => {
    setBusy(true);
    try {
      const { store } = await mk(`/s/${slug}`);
      onLogin(await mk(`/admin/stores/${store.id}/access`, { method: "POST" }));
    } catch (e2) {
      setErr(e2.message);
      setBusy(false);
    }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b1530] p-5">
      <form onSubmit={submit} className="w-full max-w-sm space-y-3 rounded-3xl bg-white p-7 shadow-2xl" data-testid="owner-login-form">
        <h1 className="text-xl font-bold text-slate-900">Administrar tienda</h1>
        <p className="text-sm text-slate-500">Entra con el correo y contraseña del dueño.</p>
        <input required type="email" className={inputCls} placeholder="Correo" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="owner-email-input" />
        <input required type="password" className={inputCls} placeholder="Contraseña" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="owner-password-input" />
        {err && <p className="text-sm text-red-600" data-testid="owner-login-error">{err}</p>}
        <button disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white" data-testid="owner-login-submit">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Entrar
        </button>
        {localStorage.getItem("uai_creator") && (
          <button type="button" onClick={creator} disabled={busy} className="w-full rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-700" data-testid="owner-creator-access">
            Entrar como creador
          </button>
        )}
      </form>
    </div>
  );
};

const TABS = [
  ["products", "Productos", Package],
  ["orders", "Pedidos", ShoppingCart],
  ["design", "Diseño", Palette],
  ["delivery", "Entrega", Truck],
  ["payments", "Pagos", CreditCard],
];

export default function StoreAdmin() {
  const { slug } = useParams();
  const [auth, setAuth] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(tokenKey(slug)));
    } catch {
      return null;
    }
  });
  const [store, setStore] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [tab, setTab] = useState("products");

  const login = (d) => {
    localStorage.setItem(tokenKey(slug), JSON.stringify({ token: d.token, sid: d.store.id }));
    setAuth({ token: d.token, sid: d.store.id });
  };
  const logout = () => {
    localStorage.removeItem(tokenKey(slug));
    setAuth(null);
  };
  const load = () =>
    mk(`/owner/${auth.sid}`, { token: auth.token })
      .then((s) => {
        setStore(s);
        setDraft(s);
      })
      .catch((e) => (e.status === 401 || e.status === 403) && logout());
  useEffect(() => {
    if (auth) load();
  }, [auth]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!auth) return <OwnerLogin slug={slug} onLogin={login} />;
  if (!draft)
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-7 w-7 animate-spin text-slate-400" />
      </div>
    );

  const dirty = JSON.stringify({ ...draft, payments_ready: 0, stripe_key_hint: 0 }) !== JSON.stringify({ ...store, payments_ready: 0, stripe_key_hint: 0 });
  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      const s = await mk(`/owner/${auth.sid}`, {
        method: "PUT",
        token: auth.token,
        body: { name: draft.name, domain: draft.domain, theme: draft.theme, texts: draft.texts, contact: draft.contact, shipping: draft.shipping, status: draft.status },
      });
      setStore(s);
      setDraft(s);
      setMsg("Cambios guardados");
    } catch (e) {
      setMsg(e.message);
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 2500);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      <header className="safe-top sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="truncate font-bold text-slate-900" data-testid="owner-store-name">{store.name}</p>
            <p className="text-xs text-slate-500">Panel de la tienda</p>
          </div>
          <Link to={`/m/${slug}`} target="_blank" className="flex items-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700" data-testid="owner-view-store">
            <ExternalLink className="h-3.5 w-3.5" /> Ver tienda
          </Link>
          <button onClick={logout} className="rounded-full p-2 text-slate-500 hover:bg-slate-100" data-testid="owner-logout" aria-label="Salir">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 pt-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 grid h-auto w-full grid-cols-5 rounded-2xl bg-white p-1 shadow-sm">
            {TABS.map(([v, l, Icon]) => (
              <TabsTrigger key={v} value={v} className="flex flex-col gap-0.5 rounded-xl py-2 text-[11px] data-[state=active]:bg-blue-600 data-[state=active]:text-white sm:text-xs" data-testid={`owner-tab-${v}`}>
                <Icon className="h-4 w-4" /> {l}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="products"><ProductsTab sid={auth.sid} token={auth.token} cur={store.currency} /></TabsContent>
          <TabsContent value="orders"><OrdersTab sid={auth.sid} token={auth.token} cur={store.currency} /></TabsContent>
          <TabsContent value="design"><DesignTab draft={draft} setDraft={setDraft} /></TabsContent>
          <TabsContent value="delivery"><DeliveryTab draft={draft} setDraft={setDraft} /></TabsContent>
          <TabsContent value="payments"><PaymentsTab sid={auth.sid} token={auth.token} store={store} onChange={load} /></TabsContent>
        </Tabs>
      </main>
      {(dirty || msg) && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 p-3 backdrop-blur" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
          <div className="mx-auto flex max-w-3xl items-center gap-3">
            <p className="flex-1 text-sm text-slate-600" data-testid="owner-save-msg">{msg || "Tienes cambios sin guardar"}</p>
            {dirty && (
              <button onClick={save} disabled={saving} className="flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg" data-testid="owner-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
